import { clsx } from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';

import styles from './SecondaryNav.module.scss';
import { BetaSecondaryNavContext } from './SecondaryNavContext';

export type BetaNavItemKey = string | number;

type RootProps = {
  /** What to render in the `BetaSecondaryNavItem`. */
  children: ReactNode;
  /** Optional property for overriding styles. */
  className?: string;
  /** Key of the item that should be selected on mount. */
  defaultSelectedItem?: BetaNavItemKey | null;
  /** Key(s) of the section(s) that should be expanded on mount. */
  defaultExpandedKeys?: BetaNavItemKey[];
  /** Currently selected item key (only use if controlling state) */
  selectedItem?: BetaNavItemKey | null;
  /** Called when selection changes (only use if controlling state) */
  onSelectedItemChange?: (key: BetaNavItemKey | null) => void | null;
  /** Currently expanded section keys (only use if controlling state) */
  expandedKeys?: BetaNavItemKey[] | null;
  /** Called when expanded sections change (only use if controlling state) */
  onExpandedKeysChange?: (keys: BetaNavItemKey[]) => void | null;
};

/** Collapsible navigation meant to be used beside `UniversalLayout`'s side navigation. */
export function BetaSecondaryNav({
  children,
  className,
  defaultSelectedItem = null,
  defaultExpandedKeys = [],
  selectedItem,
  onSelectedItemChange,
  expandedKeys,
  onExpandedKeysChange,
}: RootProps) {
  const [localSelectedItem, setLocalSelectedItem] =
    useState(defaultSelectedItem);
  const [localExpandedKeys, setLocalExpandedKeys] =
    useState(defaultExpandedKeys);

  const isSelectedItemControlled = !!selectedItem;
  const isExpandedKeysControlled = !!expandedKeys;

  const currentSelectedItem = selectedItem ?? localSelectedItem;
  const currentExpandedKeys = expandedKeys ?? localExpandedKeys;

  const state = useMemo(
    () => ({
      selectedItemKey: currentSelectedItem,
      expandedSectionKeys: currentExpandedKeys,
      registerSelectedItemKey: (key: BetaNavItemKey | null) => {
        onSelectedItemChange?.(key);
        if (!isSelectedItemControlled) {
          setLocalSelectedItem(key);
        }
      },
      registerExpandedSectionKeys: (keys: BetaNavItemKey[]) => {
        onExpandedKeysChange?.(keys);
        if (!isExpandedKeysControlled) {
          setLocalExpandedKeys(keys);
        }
      },
    }),
    [
      currentSelectedItem,
      currentExpandedKeys,
      isSelectedItemControlled,
      isExpandedKeysControlled,
      onSelectedItemChange,
      onExpandedKeysChange,
    ]
  );

  return (
    <BetaSecondaryNavContext.Provider value={state}>
      <nav className={clsx(styles.secondaryNav, className)} data-zds>
        {children}
      </nav>
    </BetaSecondaryNavContext.Provider>
  );
}
